import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { TbxTooltip } from '@/common/components'

const classes = {
    formControl_medium: {
        width: '180px',
    },
}

/**
 * Cutting technology selector for quote items.
 * Displays a dropdown to select the cutting technology (e.g., Laser, Waterjet).
 * Memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param {Object} props - Component props
 * @param {string} props.value - The currently selected cutting technology ID
 * @param {Array<{cuttingTechnologyId: string, name: string}>} props.options - Available cutting technology options
 * @param {boolean} [props.disabled=false] - Whether the selector is disabled
 * @param {boolean} [props.isPartFromLibrary=false] - Whether the part is from the part library
 * @param {Function} props.onChange - Callback when selection changes
 * @returns {React.ReactElement} A row with label and cutting technology dropdown
 */
const CuttingTechnologySelector = ({ disabled = false, isPartFromLibrary = false, onChange, options, value }) => {
    const { t } = useTranslation()

    const tooltipTitle = isPartFromLibrary ? t('Cannot edit parts from library') : ''

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Typography data-testid="nqp-quote-item-cutting-technology-label">{t('Cutting technology')}</Typography>
            <TbxTooltip
                title={tooltipTitle}
                arrow
            >
                <FormControl
                    color="primary"
                    margin="none"
                    size="small"
                    sx={classes.formControl_medium}
                    variant="outlined"
                >
                    <Select
                        disabled={disabled}
                        id="select-itemCuttingTechnology"
                        inputProps={{ 'data-testid': 'nqp-quote-item-cutting-technology' }}
                        labelId="select-itemCuttingTechnology-label"
                        SelectDisplayProps={{ style: { textAlign: 'left' } }}
                        value={value}
                        onChange={(e) => onChange(e)}
                    >
                        {options?.map((ct) => (
                            <MenuItem
                                key={ct.cuttingTechnologyId}
                                value={ct.cuttingTechnologyId}
                            >
                                {ct.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </TbxTooltip>
        </Box>
    )
}

CuttingTechnologySelector.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    isPartFromLibrary: PropTypes.bool,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            cuttingTechnologyId: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ),
}

export default memo(CuttingTechnologySelector)
